import { useEffect, useMemo, useState } from 'react';
import { getProjectById } from 'api/projects/getProjects';

/**
 * The project a viewer loads its model from.
 *
 * The projects list is slim (api/projects/getProjects): its rows say how
 * many model files there are but not which. `listProject` is this page's
 * row from redux, or null while the list is still on its way; whenever it
 * is missing or slim, the project is fetched on its own (~0.3 s) - which
 * also means a viewer no longer waits for the list to start.
 *
 * With both in hand the list row stays the base - shares, milestones and
 * other edits the page makes live in redux - and the fetched copy supplies
 * only the file lists (zipInfo, export history, each timeline's zipInfo).
 * A full row (the admin lists) is returned as it is. Null until known.
 */
export default function useProjectFiles(projectId, listProject) {
  const needFetch = !!projectId && (!listProject || !!listProject.filesOmitted);
  const [fetched, setFetched] = useState(null);

  useEffect(() => {
    if (!needFetch) return undefined;
    if (fetched && String(fetched._id) === String(projectId)) return undefined;
    let alive = true;
    getProjectById(projectId)
      .then((p) => { if (alive && p && String(p._id) === String(projectId)) setFetched(p); })
      .catch(() => { /* a list row without files stays slim; nothing to load */ });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, needFetch]);

  return useMemo(() => {
    if (listProject && !listProject.filesOmitted) return listProject;
    if (!fetched || String(fetched._id) !== String(projectId)) return null;
    if (!listProject) return fetched;
    const timelinesById = new Map((fetched.timelines || []).map((t) => [String(t._id), t]));
    return {
      ...listProject,
      zipInfo: fetched.zipInfo ?? listProject.zipInfo,
      allZipVersions: fetched.allZipVersions ?? listProject.allZipVersions,
      timelines: (listProject.timelines || []).map((t) => {
        const full = timelinesById.get(String(t._id));
        return full && full.zipInfo ? { ...t, zipInfo: full.zipInfo } : t;
      }),
      filesOmitted: false,
    };
  }, [listProject, fetched, projectId]);
}
